import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiEdit3, FiGripVertical, FiCheck } from 'react-icons/fi';

const EditableChecklist = ({ tasks, setTasks, title, description, icon: Icon, colorClass }) => {
  const [editingId, setEditingId] = useState(null);

  const addTask = () => {
    const newTask = {
      id: `task-${Date.now()}`,
      title: 'New Task',
      description: 'Click to edit description',
      specs: 'New'
    };
    setTasks([...tasks, newTask]);
    setEditingId(newTask.id);
  };

  const removeTask = (id) => {
    if (window.confirm('Delete this task from the master list?')) {
      setTasks(tasks.filter(t => t.id !== id));
    }
  };

  const moveTask = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tasks.length) return;
    const newTasks = [...tasks];
    const [movedTask] = newTasks.splice(index, 1);
    newTasks.splice(newIndex, 0, movedTask);
    setTasks(newTasks);
  };

  const updateTask = (id, field, value) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-cream-dark overflow-hidden">
      <div className="p-6 border-b border-cream-dark bg-cream/10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center text-white`}>
              <SafeIcon icon={Icon} className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-espresso">{title}</h3>
              <p className="text-xs text-espresso-light/60">{description}</p>
            </div>
          </div>
          <button 
            onClick={addTask}
            className="flex items-center gap-2 px-4 py-2 bg-espresso text-white rounded-xl text-xs font-bold hover:bg-espresso-light transition-all shadow-sm"
          >
            <SafeIcon icon={FiPlus} /> Add Task
          </button>
        </div>
      </div>

      <div className="divide-y divide-cream-dark">
        {tasks.map((task, index) => (
          <div key={task.id} className="p-4 hover:bg-cream/20 transition-colors group">
            <div className="flex items-start gap-4">
              <div className="flex flex-col gap-1 mt-1">
                <button 
                  disabled={index === 0}
                  onClick={() => moveTask(index, -1)}
                  className={`p-1 rounded hover:bg-cream-dark ${index === 0 ? 'opacity-20' : 'text-espresso-light'}`}
                >
                  <SafeIcon icon={FiChevronUp} className="w-3 h-3" />
                </button>
                <button 
                  disabled={index === tasks.length - 1}
                  onClick={() => moveTask(index, 1)}
                  className={`p-1 rounded hover:bg-cream-dark ${index === tasks.length - 1 ? 'opacity-20' : 'text-espresso-light'}`}
                >
                  <SafeIcon icon={FiChevronDown} className="w-3 h-3" />
                </button>
              </div>

              <div className="flex-1">
                {editingId === task.id ? (
                  <div className="space-y-3 animate-fade-in">
                    <input 
                      autoFocus
                      className="w-full text-sm font-bold border-b border-terracotta bg-transparent focus:outline-none pb-1"
                      value={task.title}
                      onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                    />
                    <textarea 
                      className="w-full text-xs text-espresso-light border border-cream-dark rounded-lg p-2 focus:outline-none focus:border-terracotta"
                      value={task.description}
                      onChange={(e) => updateTask(task.id, 'description', e.target.value)}
                      rows={2}
                    />
                    <div className="flex justify-between items-center">
                      <input 
                        className="text-[10px] font-bold uppercase tracking-wider text-terracotta border-b border-cream-dark px-1 bg-transparent focus:outline-none"
                        value={task.specs || ''}
                        placeholder="TAG (e.g. 300 DPI)"
                        onChange={(e) => updateTask(task.id, 'specs', e.target.value)}
                      />
                      <button 
                        onClick={() => setEditingId(null)}
                        className="text-[10px] font-bold text-sage flex items-center gap-1 hover:underline"
                      >
                        <SafeIcon icon={FiCheck} /> Done Editing
                      </button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => setEditingId(task.id)} className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-espresso text-sm">{task.title}</h4>
                      {task.specs && (
                        <span className="text-[9px] font-bold uppercase bg-cream px-1.5 py-0.5 rounded border border-terracotta/20 text-terracotta">
                          {task.specs}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-espresso-light leading-relaxed">{task.description}</p>
                  </div>
                )}
              </div>

              <button 
                onClick={() => removeTask(task.id)}
                className="opacity-0 group-hover:opacity-100 p-2 text-espresso-light/20 hover:text-red-500 transition-all"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditableChecklist;